import { isService } from './service'
import type { SupabaseClient } from '@supabase/supabase-js'
import { t } from './i18n'

/**
 * Counting a stocktake — the one path every screen uses (migration 069).
 *
 * Several phones may count the same stocktake at once, and when one product
 * stands on two shelves their counts ADD. So a count is never "the product's
 * number": it is THIS DEVICE's number for that product, and the database sums
 * the devices under a row lock. Writing `stocktake_items.counted` from a
 * screen's own copy — what the dialog did before 069 — is exactly the lost
 * update that made two phones erase each other.
 */

const DEVICE_KEY = 'stocktake-device-id'

/**
 * A random id per BROWSER, not per user: two phones signed in with the same
 * owner account are still two people counting two shelves. Kept in
 * localStorage so a reload, a locked screen or a closed tab resumes the same
 * share of the count instead of starting a second one.
 *
 * null when the browser will not keep it (private mode, blocked storage): a
 * throw-away id would make every reload a NEW phone whose share adds to the
 * old one — the count would double. The screen refuses to count instead.
 */
export function getDeviceId(): string | null {
  try {
    const existing = localStorage.getItem(DEVICE_KEY)
    if (existing) return existing
    const id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`).slice(0, 64)
    localStorage.setItem(DEVICE_KEY, id)
    return localStorage.getItem(DEVICE_KEY) === id ? id : null
  } catch {
    return null
  }
}

/** „Android · 3fa2" — enough for an owner to tell two phones apart. */
export function deviceLabel(id: string): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const kind = /iPhone|iPad/i.test(ua) ? 'iPhone' : /Android/i.test(ua) ? 'Android' : 'კომპიუტერი'
  return `${kind} · ${id.slice(-4)}`
}

const NO_DEVICE = { message: 'NO_DEVICE_STORAGE' }

/**
 * One product as THIS phone sees it (155). `mine` is null when this phone has
 * no record — a record of 0 („not on the shelf") is a real count. For a
 * cashier `total` is her own number and `expected` never arrives: another
 * phone's number reaches her only as the `othersCounted` flag.
 */
export type CountedRow = {
  product_id: string
  product_name: string
  mine: number | null
  total: number
  othersCounted: boolean
  /** The frozen number the result compares with — the owner's only. */
  expected: number | null
}

const numOrNull = (v: unknown) => (v === null || v === undefined ? null : Number(v))

export function mapCountedRow(r: Record<string, unknown>): CountedRow {
  return {
    product_id: String(r.product_id),
    product_name: String(r.product_name ?? ''),
    mine: numOrNull(r.mine),
    total: Number(r.total ?? 0),
    othersCounted: !!r.others_counted,
    expected: numOrNull(r.expected),
  }
}

/**
 * `add`       this device += qty   (a scan, the + / − buttons)
 * `set`       this device := qty   (typed on the phone: "I counted 24")
 * `set_total` this device := qty − everyone else (the owner's "the total is X")
 * `remove`    withdraw this device's number; the product leaves the count when
 *             no device has one. This is what undo must be — a product left at
 *             0 means "we found none" and would zero its stock on apply.
 */
export type CountMode = 'add' | 'set' | 'set_total' | 'remove'

/** A stocktake_items row as the RPC returns it (numerics may be strings).
 *  A cashier's lacks `counted`, `expected` and `unit_cost` (155). */
export type ItemRow = Record<string, unknown> & { id: string; counted?: number | string }

export type CountResult =
  | { removed: true; product_id: string }
  | {
      removed?: false
      product_id: string
      /** The stocktake_items row. An owner gets all of it; a cashier gets it
       *  without `expected`, `unit_cost` and `counted` (155). */
      item: ItemRow
      mine: number | null
      total: number
      othersCounted: boolean
      expected: number | null
    }

export async function countStock(
  supabase: SupabaseClient,
  args: { stocktakeId: string; productId: string; qty?: number; mode?: CountMode },
): Promise<{ data: CountResult | null; error: { message?: string } | null }> {
  const deviceId = getDeviceId()
  if (!deviceId) return { data: null, error: NO_DEVICE }
  const { data, error } = await supabase.rpc('stocktake_count', {
    p_stocktake_id: args.stocktakeId,
    p_product_id: args.productId,
    p_device_id: deviceId,
    p_qty: args.qty ?? 1,
    p_mode: args.mode ?? 'add',
    p_device_label: deviceLabel(deviceId),
  })
  if (error || !data) return { data: null, error: error ?? { message: 'empty' } }
  const r = data as Record<string, unknown>
  if (r.removed) return { data: { removed: true, product_id: String(r.product_id) }, error: null }
  const item = r.item as ItemRow
  return {
    data: {
      product_id: String(r.product_id),
      item,
      mine: numOrNull(r.mine),
      total: Number(r.total ?? 0),
      othersCounted: !!r.others_counted,
      expected: numOrNull((item as Record<string, unknown>).expected),
    },
    error: null,
  }
}

/**
 * What the counting screen loads — the same call for an owner and for a
 * cashier (070, `stocktake_state`; shaped by role since 155).
 */
export type StocktakeState = {
  /** false: a cashier whose owner has not allowed staff to count. */
  allowed: boolean
  /** Start, cancel and complete are the owner's. */
  canManage: boolean
  stocktake: { id: string; status: 'open'; category_id: string | null; branch_id: string | null; started_at: string } | null
  items: CountedRow[]
}

export async function loadStocktakeState(
  supabase: SupabaseClient,
  branchId: string | null,
): Promise<{ data: StocktakeState | null; error: { message?: string } | null }> {
  const deviceId = getDeviceId()
  if (!deviceId) return { data: null, error: NO_DEVICE }
  const { data, error } = await supabase.rpc('stocktake_state', {
    p_branch_id: branchId,
    p_device_id: deviceId,
  })
  if (error || !data) return { data: null, error: error ?? { message: 'empty' } }
  const r = data as Record<string, any>
  return {
    data: {
      allowed: !!r.allowed,
      canManage: !!r.can_manage,
      stocktake: r.stocktake ?? null,
      items: ((r.items ?? []) as Record<string, unknown>[]).map(mapCountedRow),
    },
    error: null,
  }
}

/** A stocktake_count refusal in words, in the device's language. */
export function countErrorText(message?: string): string {
  const m = message ?? ''
  if (m.includes('NO_DEVICE_STORAGE')) return t('wh.x.countErrNoStorage')
  if (m.includes('NOT_ALLOWED')) return t('wh.x.countErrNotAllowed')
  if (m.includes('ORG_INACTIVE')) return t('store.refusal.orgInactive')
  if (m.includes('STOCKTAKE_ALREADY_COMPLETED')) return t('wh.x.countErrDone')
  if (m.includes('STOCKTAKE_NOT_FOUND')) return t('wh.x.countErrGone')
  if (m.includes('PRODUCT_NOT_FOUND')) return t('store.x.41')
  return t('wh.x.countErrSave')
}

/** „12", „1.25" — a count as the stocktake screens print it. */
export const fmtQty = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/\.?0+$/, ''))

/** A count this large is a barcode that landed in the number field. */
export const MAX_COUNT = 99999

/**
 * What a person typed into the quantity field. Pieces are whole; kilos,
 * metres and litres take a comma or a point. 0 is an answer only when it
 * SETS a number („ჯამის შეცვლა"); added, it would add nothing — the field is
 * asking how many were found.
 */
export function parseCount(
  raw: string,
  opts: { weighed: boolean; allowZero: boolean },
): { ok: true; value: number } | { ok: false; why: 'empty' | 'bad' | 'barcode' } {
  const s = raw.trim().replace(',', '.')
  if (s === '') return { ok: false, why: 'empty' }
  if (!/^\d+(\.\d+)?$/.test(s)) return { ok: false, why: 'bad' }
  const v = Number(s)
  if (!Number.isFinite(v)) return { ok: false, why: 'bad' }
  if (!opts.weighed && !Number.isInteger(v)) return { ok: false, why: 'bad' }
  if (v > MAX_COUNT) return { ok: false, why: 'barcode' }
  if (v === 0 && !opts.allowZero) return { ok: false, why: 'empty' }
  return { ok: true, value: v }
}

/** The scanner hook's own rule (hooks/use-barcode-scanner.ts): keys closer
 *  than this are a machine, not a thumb. */
const SCAN_GAP_MS = 30

/** Add one printable key's time to the current burst; a slow key starts a new one. */
export function scanBurst(burst: number[], now: number): number[] {
  const last = burst[burst.length - 1]
  return last !== undefined && now - last <= SCAN_GAP_MS ? [...burst, now] : [now]
}

/** Four keys or more, each within 30 ms of the last: a gun, even for a short
 *  shop code such as a scale's „4852". No person types that fast. */
export function isScanBurst(burst: number[]): boolean {
  return burst.length >= 4
}

/**
 * The counting screen's two tabs. „To count" = no phone has a record (sorted
 * by name); „counted" = any phone has one — also a product only ANOTHER phone
 * counted, so two phones never both see it as still to do. `done / all` is
 * the scope's progress; a product counted outside the scope (found by search)
 * is listed but not in it.
 */
export function splitRows<P extends { id: string; name: string; is_service?: boolean | null }>(
  all: P[],
  counted: Map<string, CountedRow>,
): { left: P[]; counted: CountedRow[]; done: number; all: number } {
  const scope = all.filter((p) => !isService(p))   // 169 — a service is never counted
  const left = scope.filter((p) => !counted.has(p.id)).sort((a, b) => a.name.localeCompare(b.name))
  return {
    left,
    counted: [...counted.values()].sort((a, b) => a.product_name.localeCompare(b.product_name)),
    done: scope.length - left.length,
    all: scope.length,
  }
}

/**
 * The quantity sheet's secondary actions. „Not on the shelf — 0" only while
 * this phone has no record (after a count it would contradict it); removing
 * and changing the total whenever it has one — a record of 0 included.
 */
export function sheetActions(row: CountedRow | undefined) {
  const has = row?.mine !== null && row?.mine !== undefined
  return { showZero: !has, showRemove: has, showChangeTotal: has }
}

/** A stocktake_items row as the owner's result reads it. */
export type ReviewItem = { id: string; product_id: string | null; product_name: string; expected: number; counted: number; unit_cost: number }

/**
 * The result in numbers — the same arithmetic as complete_stocktake
 * (difference × unit_cost), so the screen and the saved totals agree. The
 * differing rows come biggest money first.
 */
export function resultSummary(items: ReviewItem[]) {
  let shortCount = 0, shortValue = 0, surplusCount = 0, surplusValue = 0, matching = 0
  for (const i of items) {
    const d = i.counted - i.expected
    if (d < 0) { shortCount++; shortValue += -d * i.unit_cost }
    else if (d > 0) { surplusCount++; surplusValue += d * i.unit_cost }
    else matching++
  }
  const diffs = items.filter((i) => i.counted !== i.expected)
    .sort((a, b) => Math.abs((b.counted - b.expected) * b.unit_cost) - Math.abs((a.counted - a.expected) * a.unit_cost))
  return { shortCount, shortValue, surplusCount, surplusValue, matching, diffs }
}

/** What the owner's „was the count finished?" must say. */
export function finishNotes(uncounted: number, counted: Iterable<CountedRow>) {
  let otherPhones = false
  for (const c of counted) if (c.othersCounted) { otherPhones = true; break }
  return { uncounted, otherPhones }
}

// ── Codex's review of the client (2026-10-07) ────────────────────────────────

/**
 * Which rows this phone changed after a moment — so a poll that LEFT before a
 * save cannot answer after it and put the old number back. A counter, not the
 * clock: two saves in the same millisecond still order.
 */
export function createMutationClock() {
  let tick = 0
  const touched = new Map<string, number>()
  return {
    /** The moment a poll is sent. */
    now: () => tick,
    /** A save of this product started or answered. */
    touch: (productId: string) => { touched.set(productId, ++tick) },
    touchedSince: (productId: string, sentAt: number) => (touched.get(productId) ?? 0) > sentAt,
  }
}

/**
 * A poll's answer laid over the screen's rows. Rows `keepLocal` names stay as
 * the screen has them (a save in flight, or one that answered after the poll
 * left — its answer is newer); every other row is the server's, and a row the
 * server no longer has (removed on another phone) goes.
 */
export function mergePolled(
  prev: Map<string, CountedRow>,
  items: CountedRow[],
  keepLocal: (productId: string) => boolean,
): Map<string, CountedRow> {
  const next = new Map<string, CountedRow>()
  for (const r of items) next.set(r.product_id, keepLocal(r.product_id) && prev.has(r.product_id) ? prev.get(r.product_id)! : r)
  for (const [pid, c] of prev) if (!next.has(pid) && keepLocal(pid)) next.set(pid, c)
  return next
}

/**
 * Every save this phone sent, until it answers. Finishing waits for all of
 * them — not for a timeout — and a save that failed keeps its product named
 * until a later save of it succeeds, so „შენი თვლა შენახულია" is never said
 * over a number the server does not have.
 */
export function createSaveTracker() {
  const inflight = new Map<Promise<unknown>, string>()
  const failed = new Map<string, string>()
  return {
    track<T extends { ok: boolean }>(productId: string, name: string, p: Promise<T>): Promise<T> {
      const watched = p.then(
        (r) => { if (r.ok) failed.delete(productId); else failed.set(productId, name); return r },
        (e) => { failed.set(productId, name); throw e },
      ).finally(() => { inflight.delete(watched) })
      inflight.set(watched, productId)
      return watched
    },
    isSaving: (productId: string) => [...inflight.values()].includes(productId),
    /** Resolves when nothing is in flight; the names of the products still unsaved. */
    async settle(): Promise<string[]> {
      while (inflight.size > 0) await Promise.allSettled([...inflight.keys()])
      return [...failed.values()]
    },
  }
}

/** The rows the owner looked at, and the rows about to be applied — the same? */
export function sameReview(a: ReviewItem[], b: ReviewItem[]): boolean {
  if (a.length !== b.length) return false
  const key = (i: ReviewItem) => `${i.product_id}|${i.counted}|${i.expected}`
  const seen = new Set(a.map(key))
  return b.every((i) => seen.has(key(i)))
}
